import { User } from '@src/entities/User/ui/components/User';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { isEmpty, merge } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import fields from './includes/fields.json';

const { codeDesc, codeId, isValid } = fields;

/**
 * FormModal
 *
 * Prompts the user with a popup window allowing them to edit the initiative.
 *
 * @param currentEntity
 * @param handleSave
 * @param isOpen
 * @param setCurrentEntity
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function FormModal({
  currentEntity = {},
  handleSave = async () => {},
  isOpen = false,
  setCurrentEntity = () => {},
  setIsOpen = () => {},
}) {
  let mode = 'create';

  if (!isEmpty(currentEntity)) {
    mode = 'edit';
  }

  const defaultValues = {
    categoryCode: 'InitiativeType',
    codeDesc: '',
    codeId: '',
    isValid: true,
  };

  const [initialValues, setInitialValues] = useState({});

  const form = useForm({ defaultValues });

  const { hasPermission } = useCurrentUser();

  const canCreate = hasPermission('Admin:Initiatives', 'create');
  const canEdit = hasPermission('Admin:Initiatives', 'edit');

  const canWrite = useMemo(() => {
    if (mode === 'create') {
      return canCreate;
    }

    if (mode === 'edit') {
      return canEdit;
    }
  }, [canEdit, canCreate, mode]);

  const statusValue = form?.getValues('isValid');

  const canActivate = hasPermission('Admin:Initiatives', 'activate');
  const canDeactivate = hasPermission('Admin:Initiatives', 'deactivate');

  const canToggle = useMemo(() => {
    if (statusValue === true) {
      return canDeactivate;
    }

    if (statusValue === false) {
      return canActivate;
    }
  }, [statusValue, canDeactivate, canActivate]);

  /**
   * Mapping data loaded from API to the form state.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      const data = merge({}, defaultValues, {
        categoryCode: currentEntity?.categoryCode,
        codeDesc: currentEntity?.codeDesc,
        codeId: currentEntity?.codeId,
        isValid: currentEntity?.isValid,
      });

      if (isEmpty(initialValues)) {
        setInitialValues(data);
      }

      form.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });
    }
  }, [currentEntity]);

  /**
   * handleClose
   *
   * Handles the closing of the modal.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    setIsOpen(false);
    setCurrentEntity({});
  };

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @param submittedValues
   * @returns {Promise<void>}
   */
  const handleSubmit = async (submittedValues) => {
    const payload = {};

    payload.codeId = String(submittedValues?.codeId);
    payload.categoryCode = String(submittedValues?.categoryCode);
    payload.codeDesc = String(submittedValues?.codeDesc);
    payload.isValid = Boolean(submittedValues?.isValid);

    await handleSave({ data: payload, id: currentEntity?.id, mode });

    await handleClose();
  };

  const formValues = form.getValues();

  /**
   * isEqual
   *
   * Compares two objects to determine if they are equal.
   *
   * @param object1
   * @param object2
   * @returns {boolean}
   */
  const isEqual = (object1, object2) => {
    return JSON.stringify(object1) === JSON.stringify(object2);
  };

  /**
   * form validation rules, toggling the save button enablement.
   */
  const isDisabled = useMemo(() => {
    return !form.formState.isDirty || isEqual(formValues, initialValues) || !isEmpty(form.formState.errors);
  }, [form.formState.isDirty, formValues, initialValues, form.formState.errors]);

  return (
    <ErrorHandler location="src/entities/Initiative/ui/components/FormModal/FormModal.jsx">
      <Modal isOpen={isOpen} onClose={handleClose} title={mode === 'edit' ? 'Edit Initiative' : 'Create Initiative'}>
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Grid>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <User.Capability
                  alias="write"
                  attributes={['create', 'edit']}
                  isAllowed={canWrite}
                  resource="Admin:Initiatives"
                >
                  <TextInput
                    {...codeId}
                    isDisabled={mode === 'edit'}
                    onChange={() => {
                      form?.validate(
                        `codeId`,
                        () => {},
                        () => {}
                      );
                    }}
                  />
                </User.Capability>
              </Grid.Col>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <User.Capability
                  alias="write"
                  attributes={['create', 'edit']}
                  isAllowed={canWrite}
                  resource="Admin:Initiatives"
                >
                  <TextInputArea
                    {...codeDesc}
                    onChange={() => {
                      form?.validate(
                        `codeDesc`,
                        () => {},
                        () => {}
                      );
                    }}
                  />
                </User.Capability>
              </Grid.Col>
              <Grid.Col span={{ xs: '100%' }}>
                <User.Capability
                  alias="toggle"
                  attributes={['activate', 'deactivate']}
                  isAllowed={canToggle}
                  resource="Admin:Initiatives"
                >
                  <ToggleSwitch {...isValid} />
                </User.Capability>
              </Grid.Col>
            </Grid>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button data-testid="action-button" isDisabled={isDisabled} type="submit" variant="solid">
                {mode === 'edit' ? 'Save' : 'Create'}
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
